import { useEffect, useState } from 'react';

/**
 * Below this visible height, in CSS pixels, the popover cannot show enough
 * rows: react-aria sizes it to the space left under the field, with no minimum.
 */
export const TRAY_MAX_VISIBLE_HEIGHT = 500;

function visibleHeight(): number {
  return window.visualViewport?.height ?? window.innerHeight;
}

function hasCoarsePointer(): boolean {
  // The DOM types say matchMedia always exists, but jsdom has none. Without it
  // every consumer's test would crash instead of rendering the popover.
  if (typeof window.matchMedia !== 'function') return false;
  return window.matchMedia('(pointer: coarse)').matches;
}

function prefersTray(threshold: number): boolean {
  return hasCoarsePointer() || visibleHeight() < threshold;
}

/**
 * Whether the combobox should open as a tray instead of a popover.
 *
 * @remarks
 * A coarse pointer always gets the tray, because on a touch device the
 * on-screen keyboard takes the space the popover needs. Otherwise the tray is
 * used while the visible viewport is lower than `threshold`. The value follows
 * resizes; freezing it while the field is open is up to the caller.
 *
 * @param threshold - The visible height in CSS pixels below which the tray is
 *   preferred. Defaults to {@link TRAY_MAX_VISIBLE_HEIGHT}.
 * @returns `true` when the tray is preferred.
 */
export function usePrefersTray(
  threshold: number = TRAY_MAX_VISIBLE_HEIGHT,
): boolean {
  const [value, setValue] = useState(() => prefersTray(threshold));
  useEffect(() => {
    const update = () => setValue(prefersTray(threshold));
    update();
    window.visualViewport?.addEventListener('resize', update);
    window.addEventListener('resize', update);
    return () => {
      window.visualViewport?.removeEventListener('resize', update);
      window.removeEventListener('resize', update);
    };
  }, [threshold]);
  return value;
}
